import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type {
  AnsprechpartnerPaare, AnsprechpartnerPaareSetzen, Ansprechpartnerbezug, ApiClient,
} from "@kundeninfo/api-client";
import { Modal, ModalFuss, useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Hinweis, Objekt, Verweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "./meHuellen.js";
import { ObjektMenue } from "./objektPanel/ObjektMenue.js";
import { errorProp } from "./fieldErrors.js";
import { useInSection } from "./Vorgangsabschnitt.js";
import { REPRESENTATIVE_UI_TEXT as T } from "./representativeTexts.js";
import "./RepresentativePairs.css";

/**
 * Der Abschnitt „ANSPRECHPARTNER“ an Angebot und Auftrag (Spec 2026-11-04-ansprechpartner-paare, „Oberfläche“): je
 * Bereich eine Objektzeile (Bereich · Auftragnehmer · Auftraggeber), Bearbeiten im rechts angeschlagenen Modalpanel,
 * Knöpfe links unten (Panel → „⋯“, Hausregel 109), Rückfrage vor dem Speichern. Gespeichert wird die GANZE Liste mit der
 * Vorgangsversion (PUT); die neue Version geht über `onVersionChange` an den Rahmen — dieselbe Naht wie bei den Anlagen.
 * Was er kann, sagt die Antwort: `eingefroren` heißt „nur lesen“.
 */
export type RepresentativePairsClient = Pick<ApiClient, "getAnsprechpartner" | "setAnsprechpartner">;

/** Der Ausschnitt, wenn der Rahmen ihn hat — die Detailklienten führen ihn wahlfrei (schmale Test-Klienten bleiben gültig,
 *  Muster `Partial<FremdkontenClient>`); ohne ihn fehlt der Abschnitt. */
export function representativesClient(c: Partial<RepresentativePairsClient>): RepresentativePairsClient | null {
  return c.getAnsprechpartner === undefined || c.setAnsprechpartner === undefined ? null
    : { getAnsprechpartner: c.getAnsprechpartner, setAnsprechpartner: c.setAnsprechpartner };
}

export type RepresentativePairsProps = {
  client: RepresentativePairsClient;
  bezug: Ansprechpartnerbezug;
  idPraefix: string;
  /** Die Belegversion des Rahmens (Kopf-Speichern dreht sie ohne den Abschnitt weiter). */
  version?: number | undefined;
  verdeckt?: boolean | undefined;
  onVersionChange?: ((version: number) => void) | undefined;
  onLoaded?: ((stand: AnsprechpartnerPaare) => void) | undefined;
};

type Zeile = AnsprechpartnerPaareSetzen["kategorien"][number];
type Feld = Exclude<keyof Zeile, "categoryId">;
type Person = AnsprechpartnerPaare["auswahl"]["nutzer"][number];
/** Das offene Panel: `index` null = neuer Bereich. */
type Bearbeitung = { index: number | null; zeile: Zeile };

const LEER: Zeile = { categoryId: "", auftragnehmerHauptUserId: null, auftragnehmerStellvUserId: null,
                      auftraggeberHauptContactId: null, auftraggeberStellvContactId: null };

const alsZeile = (k: AnsprechpartnerPaare["kategorien"][number]): Zeile => ({
  categoryId: k.categoryId,
  auftragnehmerHauptUserId: k.auftragnehmer.haupt?.id ?? null,
  auftragnehmerStellvUserId: k.auftragnehmer.stellvertretung?.id ?? null,
  auftraggeberHauptContactId: k.auftraggeber.haupt?.id ?? null,
  auftraggeberStellvContactId: k.auftraggeber.stellvertretung?.id ?? null,
});

/** Die vier Plätze im Panel: Seite, Rolle, Auswahlquelle. */
const PLAETZE: readonly { feld: Feld; seite: "contractor" | "client"; rolle: "primary" | "deputy" }[] = [
  { feld: "auftragnehmerHauptUserId", seite: "contractor", rolle: "primary" },
  { feld: "auftragnehmerStellvUserId", seite: "contractor", rolle: "deputy" },
  { feld: "auftraggeberHauptContactId", seite: "client", rolle: "primary" },
  { feld: "auftraggeberStellvContactId", seite: "client", rolle: "deputy" },
];

const meldung = (e: unknown, standard: string): string =>
  e instanceof ApiError && e.message.trim() !== "" ? e.message : standard;

export function RepresentativePairs(p: RepresentativePairsProps): ReactElement {
  const { client, bezug } = p;
  const [stand, setStand] = useState<AnsprechpartnerPaare | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [offen, setOffen] = useState<Bearbeitung | null>(null);
  const [feldFehler, setFeldFehler] = useState<Partial<Record<keyof Zeile, string>>>({});
  const version = useRef(0);
  const rueck = useRef({ v: p.onVersionChange, l: p.onLoaded });
  rueck.current = { v: p.onVersionChange, l: p.onLoaded };
  const inSection = useInSection();
  const { bestaetigen } = useBestaetigung();
  const schluessel = `${bezug.art}:${bezug.customerId}:${bezug.vorgangId}`;
  const getAnsprechpartner = client.getAnsprechpartner;

  const uebernehmen = useCallback((neu: AnsprechpartnerPaare) => {
    setStand(neu);
    version.current = neu.version;
    rueck.current.v?.(neu.version);
    rueck.current.l?.(neu);
  }, []);

  const laden = useCallback(async () => {
    try {
      uebernehmen(await getAnsprechpartner(bezug));
      setFehler(null);
    } catch (e) {
      setFehler(meldung(e, T.loadFailed));
    }
    // `schluessel` statt `bezug`: Ein Objektliteral im JSX ist bei jedem Zeichnen ein neues.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAnsprechpartner, schluessel, uebernehmen]);
  useEffect(() => { void laden(); }, [laden]);
  useEffect(() => { if (p.version !== undefined) version.current = p.version; }, [p.version]);

  /** Schreibt die ganze Liste; `true` = gespeichert. 422 am Feld der offenen Zeile, 409 lädt neu. */
  async function schreibe(kategorien: Zeile[], index: number | null): Promise<boolean> {
    setLaeuft(true);
    setHinweis(null);
    setFeldFehler({});
    try {
      uebernehmen(await client.setAnsprechpartner(bezug, { version: version.current, kategorien }));
      setFehler(null);
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setHinweis(e.code === "VERSIONSKONFLIKT" ? T.conflict : `${e.message} ${T.conflict}`);
        await laden();
        return true;
      }
      if (e instanceof ApiError && e.status === 422 && e.fields !== undefined) {
        const praefix = `kategorien[${String(index ?? kategorien.length - 1)}].`;
        const amFeld = Object.fromEntries(e.fields.filter((f) => f.field.startsWith(praefix))
          .map((f) => [f.field.slice(praefix.length), f.message]));
        setFeldFehler(amFeld);
        if (Object.keys(amFeld).length === 0) setFehler(meldung(e, T.saveFailed));
      } else {
        setFehler(meldung(e, T.saveFailed));
      }
      return false;
    } finally {
      setLaeuft(false);
    }
  }

  const zeilen = stand?.kategorien.map(alsZeile) ?? [];
  const lesend = stand?.eingefroren === true;
  const gesperrt = laeuft || p.verdeckt === true || stand === null;
  const bereichName = (id: string): string => stand?.auswahl.kategorien.find((k) => k.id === id)?.name
    ?? stand?.kategorien.find((k) => k.categoryId === id)?.name ?? "";

  async function speichern(): Promise<void> {
    if (offen === null) return;
    if (!(await bestaetigen({ titel: T.confirmSaveTitle, text: T.confirmSaveText(bereichName(offen.zeile.categoryId)),
                              bestaetigenText: T.save }))) return;
    const liste = offen.index === null ? [...zeilen, offen.zeile]
      : zeilen.map((z, i) => (i === offen.index ? offen.zeile : z));
    if (await schreibe(liste, offen.index)) setOffen(null);
  }

  async function entfernen(index: number, bereich: string): Promise<void> {
    if (!(await bestaetigen({ titel: T.confirmRemoveTitle, text: T.confirmRemoveText(bereich),
                              bestaetigenText: T.remove, gefaehrlich: true }))) return;
    await schreibe(zeilen.filter((_, i) => i !== index), null);
  }

  const oeffnen = (index: number | null): void => {
    setFeldFehler({});
    setFehler(null);
    setOffen({ index, zeile: index === null ? LEER : zeilen[index]! });
  };

  /** Die Wahl eines Platzes: die Auswahl der Seite, dazu die schon eingetragene Person, auch wenn sie nicht mehr wählbar ist. */
  function personen(seite: "contractor" | "client", gewaehlt: string | null): Person[] {
    const liste = seite === "contractor" ? stand?.auswahl.nutzer ?? [] : stand?.auswahl.kontakte ?? [];
    const eingetragen = stand?.kategorien.flatMap((k) => {
      const paar = seite === "contractor" ? k.auftragnehmer : k.auftraggeber;
      return [paar.haupt, paar.stellvertretung];
    }).find((x) => x !== null && x.id === gewaehlt);
    return eingetragen !== undefined && eingetragen !== null && !liste.some((x) => x.id === gewaehlt)
      ? [...liste, eingetragen] : liste;
  }

  const belegt = new Set(zeilen.map((z) => z.categoryId));
  const freieBereiche = (stand?.auswahl.kategorien ?? []).filter((k) => !belegt.has(k.id));

  return (
    <div className={inSection ? "kap__kasten kap__kasten--in-section" : "kap__kasten"} id={`${p.idPraefix}-ansprechpartner`}>
      {!lesend && (
        <div className="kap__kopf">
          <KiKnopf deaktiviert={gesperrt || freieBereiche.length === 0} onClick={() => oeffnen(null)}>{T.add}</KiKnopf>
        </div>
      )}
      {offen === null && fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {hinweis !== null && <Hinweis art="warnung">{hinweis}</Hinweis>}
      {stand === null && fehler === null && <p className="kap__leer" role="status">{T.loading}</p>}
      {stand !== null && stand.kategorien.length === 0 && (
        <p className="kap__leer">{lesend ? T.emptyReadOnly : T.empty}</p>
      )}
      {stand !== null && stand.kategorien.length > 0 && (
        <div className="kap__liste" aria-label={T.section}>
          {stand.kategorien.map((k, i) => (
            <Objekt key={k.categoryId} name={k.name}
              angaben={T.pairSummary(
                { haupt: k.auftragnehmer.haupt?.name ?? null, stellv: k.auftragnehmer.stellvertretung?.name ?? null },
                { haupt: k.auftraggeber.haupt?.name ?? null, stellv: k.auftraggeber.stellvertretung?.name ?? null })}
              {...(lesend ? {} : { fuss: (
                <>
                  <Verweis panel={T.edit(k.name)} onPanel={() => { if (!gesperrt) oeffnen(i); }} />
                  <ObjektMenue name={T.actions(k.name)} eintraege={[{ wert: "remove", text: T.remove, gefaehrlich: true }]}
                    onWahl={() => { if (!gesperrt) void entfernen(i, k.name); }} />
                </>
              ) })} />
          ))}
        </div>
      )}
      {lesend && <p className="kap__leer">{T.frozen}</p>}

      {offen !== null && stand !== null && (
        <Modal titel={offen.index === null ? T.titleNew : T.titleEdit(bereichName(offen.zeile.categoryId))}
          onAbbrechen={() => { if (!laeuft) setOffen(null); }} abbrechenGesperrt={laeuft}>
          {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
          <div className="kap__felder">
            <Auswahl id={`${p.idPraefix}-ap-kategorie`} name={`${p.idPraefix}-ap-kategorie`} beschriftung={T.category}
              wert={offen.zeile.categoryId} {...errorProp(feldFehler.categoryId)}
              gesperrt={offen.index !== null}
              onGeaendert={(d) => setOffen({ ...offen, zeile: { ...offen.zeile, categoryId: d.wert } })}>
              {offen.index === null && <option value="">{T.categoryChoose}</option>}
              {(offen.index === null ? freieBereiche
                : [{ id: offen.zeile.categoryId, name: bereichName(offen.zeile.categoryId) }]).map((k) => (
                <option key={k.id} value={k.id}>{k.name}</option>
              ))}
            </Auswahl>
            {(["contractor", "client"] as const).map((seite) => (
              <fieldset key={seite} className="kap__seite">
                <legend>{T[seite]}</legend>
                {PLAETZE.filter((pl) => pl.seite === seite).map((pl) => (
                  <Auswahl key={pl.feld} id={`${p.idPraefix}-ap-${pl.feld}`} name={`${p.idPraefix}-ap-${pl.feld}`}
                    beschriftung={T[pl.rolle]} wert={offen.zeile[pl.feld] ?? ""} {...errorProp(feldFehler[pl.feld])}
                    onGeaendert={(d) => setOffen({ ...offen, zeile: { ...offen.zeile, [pl.feld]: d.wert === "" ? null : d.wert } })}>
                    <option value="">{T.nobody}</option>
                    {personen(seite, offen.zeile[pl.feld]).map((x) => (
                      <option key={x.id} value={x.id}>{x.jobTitle === null ? x.name : `${x.name} (${x.jobTitle})`}</option>
                    ))}
                  </Auswahl>
                ))}
              </fieldset>
            ))}
          </div>
          <ModalFuss speichernText={T.save} speichernGesperrt={laeuft || offen.zeile.categoryId === ""}
            abbrechenGesperrt={laeuft} onSpeichern={() => { void speichern(); }}
            onAbbrechen={() => { if (!laeuft) setOffen(null); }} />
        </Modal>
      )}
    </div>
  );
}
